import { OfflineImage } from "@/components/common/offline-image/offline-image";
import type {
  DerivedPhotoEntry,
  PreconPlanImage,
} from "./derive-precon-sections";

/**
 * Numbered pin dot for the plan thumbnail — the pin-marker-dot visual
 * language (white ring, tertiary fill) sized for a static thumbnail, with the
 * photo number inside so the photo↔pin cross-reference reads at a glance.
 * Non-interactive and aria-hidden: the same numbers head the photo rows.
 */
function NumberedPinDot({
  xPct,
  yPct,
  n,
}: {
  xPct: number;
  yPct: number;
  n: number;
}) {
  return (
    <div
      data-slot="precon-pin-dot"
      aria-hidden
      className="pointer-events-none absolute flex items-center justify-center rounded-full border border-white bg-tertiary text-[10px] leading-none font-semibold text-tertiary-foreground tabular-nums shadow-md"
      style={{
        left: `${xPct * 100}%`,
        top: `${yPct * 100}%`,
        width: 18,
        height: 18,
        transform: "translate(-50%, -50%)",
      }}
    >
      {n}
    </div>
  );
}

/**
 * The plan image with every located pin drawn and numbered — the one piece
 * shared verbatim between the Sections-step section card (editing) and the
 * read-only preview, so the pins look identical in both. Renders the
 * offline-cached plan image ([[offline-image.spec]]) with a numbered dot at
 * each located record's normalized pin coordinate; the number is the record's
 * pin number (`recordNumber`), matching the `(n)` photo rows.
 *
 * **One marker per record**, not per photo: a multi-photo record contributes
 * several entries to `located` (sub-lettered `Na, Nb…`) but shares a single
 * pin, so entries are de-duplicated by `record_id` before drawing.
 *
 * `planImage === null` is the PDF's own degradation: nothing renders here, the
 * caller's metadata/notes/photos (which keep their numbers) carry the section.
 * Returns `null` in that case so the section sheet's grid collapses to one
 * column.
 */
export function PreconPlanWithPins({
  planImage,
  located,
  planName,
}: {
  planImage: PreconPlanImage | null;
  /** Located photo entries (plan + pin), capture order — one pin per record. */
  located: DerivedPhotoEntry[];
  /** Plan name — the image's alt text. */
  planName: string;
}) {
  if (!planImage) return null;
  // One marker per record: a record's photos share its pin, so keep the first
  // entry seen per record_id (entries of a record are consecutive).
  const seen = new Set<string>();
  const pins = located.filter((entry) => {
    if (entry.pin_x === null || entry.pin_y === null) return false;
    if (seen.has(entry.record_id)) return false;
    seen.add(entry.record_id);
    return true;
  });
  return (
    <div className="relative overflow-hidden rounded-md border">
      <OfflineImage
        assetKey={planImage.assetKey}
        src={planImage.src}
        alt={planName}
        className="block h-auto w-full"
        draggable={false}
      />
      {pins.map((entry) => (
        <NumberedPinDot
          key={entry.record_id}
          xPct={entry.pin_x as number}
          yPct={entry.pin_y as number}
          n={entry.recordNumber}
        />
      ))}
    </div>
  );
}
